import React, { useCallback } from 'react';
import { formatPhone } from '../../helpers/phoneUtils';
import {
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Box,
    TextField,
    Grid,
    FormControlLabel,
    Checkbox,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from '@material-ui/core';
import { useFormik } from 'formik';
import { doSwaggerCall } from '../../hooks/useSwaggerClient';
import { useNotify } from '../../hooks/useNotify';
import { formatMoney } from '../../utils';

function SendPaymentLinkModal({ showModal, MODAL_TYPE, guest, balance }) {
    const { notifySuccess, notifyError } = useNotify();

    const handleSendPaymentLink = useCallback(
        async values => {
            try {
                // Build notifyVia array
                const notifyVia = [];
                if (values.notifyEmail) {
                    notifyVia.push('email');
                }
                if (values.notifySms) {
                    notifyVia.push('sms');
                }

                const paymentData = {
                    balanceSlug: values.balanceSlug,
                    amount: parseFloat(values.amount),
                    note: values.note,
                    email: values.email,
                    notifyVia: notifyVia.length > 0 ? notifyVia : ['email']
                };

                // Only include expiresInHours if it was provided
                if (values.expiresInHours) {
                    paymentData.expiresInHours = parseFloat(values.expiresInHours);
                }

                const response = await doSwaggerCall('balance', 'sendPaymentLink', {
                    userId: guest.id,
                    paymentData
                });
                if (response.status === 'success') {
                    notifySuccess('Payment link sent successfully.');
                    showModal(MODAL_TYPE.NONE);
                }
            } catch (error) {
                console.error('Error sending payment link:', error);
                notifyError(error?.message || 'An error occurred while sending the payment link.');
            }
        },
        [MODAL_TYPE.NONE, guest.id, notifyError, notifySuccess, showModal]
    );

    // Build comma-separated email string
    const emailAddresses = [];
    if (guest?.email) {
        emailAddresses.push(guest.email);
    }
    if (guest?.additional_emails && Array.isArray(guest.additional_emails)) {
        emailAddresses.push(...guest.additional_emails);
    }
    const defaultEmailString = emailAddresses.join(', ');

    const { values, errors, isSubmitting, handleChange, setFieldValue, handleSubmit } = useFormik({
        initialValues: {
            balanceSlug: balance?.slug || '',
            email: defaultEmailString,
            amount: balance?.amount < 0 ? Math.floor(Math.abs(balance.amount) * 100) / 100 : '',
            note: '',
            expiresInHours: 12,
            notifyEmail: true,
            notifySms: false
        },
        validate: vals => {
            const errs = {};
            if (!vals.balanceSlug) {
                errs.balanceSlug = 'Please select a balance type';
            }
            if (!vals.amount || parseFloat(vals.amount) <= 0) {
                errs.amount = 'Please enter a valid amount.';
            }
            // Email is required if notifyEmail is checked
            if (vals.notifyEmail && (!vals.email || !vals.email.trim())) {
                errs.email = 'Email is required when sending via email';
            }
            // Email format validation if provided
            if (vals.email && vals.email.trim()) {
                const emails = vals.email.split(',').map(e => e.trim());
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                const invalidEmails = emails.filter(email => email && !emailRegex.test(email));
                if (invalidEmails.length > 0) {
                    errs.email = `Invalid email format: ${invalidEmails.join(', ')}`;
                }
            }
            // At least one notification method must be selected
            if (!vals.notifyEmail && !vals.notifySms) {
                errs.notifyEmail = 'Select at least one notification method';
            }
            return errs;
        },
        onSubmit: handleSendPaymentLink
    });

    return (
        <Dialog
            aria-labelledby="send-payment-link-modal"
            maxWidth="sm"
            fullWidth
            open
            onClose={() => showModal(MODAL_TYPE.NONE)}
            disableEnforceFocus
        >
            <form id="send-payment-link-form" onSubmit={handleSubmit}>
                <DialogTitle>Send Payment Link</DialogTitle>
                <DialogContent>
                    <Box>
                        <Box mb={2}>
                            <Typography variant="subtitle2" gutterBottom>
                                Send via:
                            </Typography>
                            <Grid container spacing={1}>
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.notifyEmail}
                                                onChange={e =>
                                                    setFieldValue('notifyEmail', e.target.checked)
                                                }
                                                name="notifyEmail"
                                                disabled={isSubmitting}
                                            />
                                        }
                                        label="Email"
                                    />
                                </Grid>
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.notifySms}
                                                onChange={e =>
                                                    setFieldValue('notifySms', e.target.checked)
                                                }
                                                name="notifySms"
                                                disabled={isSubmitting || !guest?.phone}
                                            />
                                        }
                                        label={`SMS${
                                            guest?.phone ? ` (${formatPhone(guest.phone)})` : ' (no phone)'
                                        }`}
                                    />
                                </Grid>
                            </Grid>
                            {errors.notifyEmail && (
                                <Typography color="error" variant="caption">
                                    {errors.notifyEmail}
                                </Typography>
                            )}
                        </Box>

                        <Box mb={2}>
                            <Typography variant="subtitle2" gutterBottom>
                                Balance Type:
                            </Typography>
                            <Typography variant="body1">
                                {balance?.name || balance?.slug || 'Unknown'} (Current Balance:{' '}
                                {formatMoney(balance?.amount)})
                            </Typography>
                        </Box>

                        <TextField
                            fullWidth
                            label="Email Addresses (comma separated)"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            disabled={isSubmitting || !values.notifyEmail}
                            error={Boolean(errors.email)}
                            helperText={
                                values.notifyEmail
                                    ? errors.email ||
                                      'Enter one or more email addresses separated by commas'
                                    : 'Email notification is disabled'
                            }
                            margin="normal"
                        />
                        <Grid container spacing={2} alignItems="start" style={{ marginTop: 16 }}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Amount ($)"
                                    name="amount"
                                    type="number"
                                    value={values.amount}
                                    onChange={handleChange}
                                    required
                                    disabled={isSubmitting}
                                    error={Boolean(errors.amount)}
                                    helperText={errors.amount}
                                    inputProps={{ step: '0.01', min: '0' }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="expires-in-hours-label">Link Expiration</InputLabel>
                                    <Select
                                        labelId="expires-in-hours-label"
                                        id="expiresInHours"
                                        name="expiresInHours"
                                        value={values.expiresInHours}
                                        onChange={handleChange}
                                        disabled={isSubmitting}
                                    >
                                        <MenuItem value={1}>1 hour</MenuItem>
                                        <MenuItem value={12}>12 hours</MenuItem>
                                        <MenuItem value={24}>24 hours</MenuItem>
                                        <MenuItem value={48}>48 hours</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Custom Message (Optional)"
                            name="note"
                            multiline
                            variant="outlined"
                            value={values.note}
                            onChange={handleChange}
                            InputProps={{
                                rows: 4
                            }}
                            disabled={isSubmitting}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="text"
                        onClick={() => showModal(MODAL_TYPE.NONE)}
                        disabled={isSubmitting}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                    >
                        Send Payment Link
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default SendPaymentLinkModal;
